/**
 * ViolationValuesFields — the pricing block of a violation (mode / amount / start fee / cap / grace)
 * with a live example. Used by the master list form and by the per-service × emirate editor.
 */
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { computeFine, fineExampleText } from './violationCalc';

const toggleNullable = (value, on) => (on ? (value ?? 0) : null);

export default function ViolationValuesFields({ values, onChange, language, testIdPrefix = 'vio', compact = false }) {
  const isAr = language === 'ar';
  const set = (patch) => onChange({ ...values, ...patch });
  const hasStart = values.start_fee !== null && values.start_fee !== undefined;
  const hasMax = values.max_fee !== null && values.max_fee !== undefined;
  const inputCls = 'h-9 bg-[#050505] border-[#2a2a4e] text-white text-sm';

  return (
    <div className={`space-y-3 ${compact ? '' : 'rounded-xl border border-white/10 bg-white/[0.02] p-4'}`} data-testid={`${testIdPrefix}-values`}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-[11px] text-white/60">{isAr ? 'نوع الاحتساب' : 'Calculation'}</label>
          <div className="flex rounded-lg border border-[#2a2a4e] overflow-hidden mt-1" data-testid={`${testIdPrefix}-mode`}>
            {['daily', 'fixed'].map((m) => (
              <button key={m} type="button" onClick={() => set({ mode: m })}
                className={`flex-1 h-9 text-xs font-bold transition-colors ${values.mode === m ? 'bg-[#d4af37] text-black' : 'bg-[#050505] text-white/70 hover:text-white'}`}
                data-testid={`${testIdPrefix}-mode-${m}`}>
                {m === 'daily' ? (isAr ? 'يومي' : 'Daily') : (isAr ? 'مبلغ ثابت' : 'Fixed')}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[11px] text-white/60">{values.mode === 'daily' ? (isAr ? 'قيمة اليوم (د.إ)' : 'Per day (AED)') : (isAr ? 'قيمة المخالفة (د.إ)' : 'Amount (AED)')}</label>
          <Input type="number" min="0" step="0.01" value={values.amount ?? ''} onChange={(e) => set({ amount: e.target.value })} className={`${inputCls} mt-1`} data-testid={`${testIdPrefix}-amount`} />
        </div>
        <div>
          <label className="text-[11px] text-white/60">{isAr ? 'مهلة المخالفة (أيام)' : 'Grace period (days)'}</label>
          <Input type="number" min="0" step="1" value={values.grace_days ?? ''} onChange={(e) => set({ grace_days: e.target.value })} placeholder="30" className={`${inputCls} mt-1`} data-testid={`${testIdPrefix}-grace`} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-lg border border-white/10 bg-black/30 p-2.5">
          <label className="flex items-center justify-between text-xs text-white/80 cursor-pointer">
            <span>{isAr ? 'هل توجد بداية لتسعيرة المخالفة؟' : 'Has a starting fee?'}</span>
            <Switch checked={hasStart} onCheckedChange={(on) => set({ start_fee: toggleNullable(values.start_fee, on) })} data-testid={`${testIdPrefix}-start-switch`} />
          </label>
          {hasStart && <Input type="number" min="0" step="0.01" value={values.start_fee ?? ''} onChange={(e) => set({ start_fee: e.target.value })} placeholder={isAr ? 'مثال: 200' : 'e.g. 200'} className={`${inputCls} mt-2`} data-testid={`${testIdPrefix}-start-fee`} />}
        </div>
        <div className="rounded-lg border border-white/10 bg-black/30 p-2.5">
          <label className="flex items-center justify-between text-xs text-white/80 cursor-pointer">
            <span>{isAr ? 'هل توجد نهاية (سقف) للمخالفة؟' : 'Has a maximum cap?'}</span>
            <Switch checked={hasMax} onCheckedChange={(on) => set({ max_fee: toggleNullable(values.max_fee, on) })} data-testid={`${testIdPrefix}-max-switch`} />
          </label>
          {hasMax && <Input type="number" min="0" step="0.01" value={values.max_fee ?? ''} onChange={(e) => set({ max_fee: e.target.value })} placeholder={isAr ? 'مثال: 1000' : 'e.g. 1000'} className={`${inputCls} mt-2`} data-testid={`${testIdPrefix}-max-fee`} />}
        </div>
      </div>

      <div className="rounded-lg bg-[#d4af37]/10 border border-[#d4af37]/30 px-3 py-2 text-[11px] text-[#e8d48b]" data-testid={`${testIdPrefix}-example`}>
        {fineExampleText(values, isAr)}
        <span className="text-white/40"> · {isAr ? 'بعد 10 أيام محتسبة' : 'after 10 counted days'}: {computeFine(values, Number(values.grace_days || 0) + 10).toLocaleString()} {isAr ? 'د.إ' : 'AED'}</span>
      </div>
    </div>
  );
}
